import * as THREE from "three";
import { releaseAndDisposeSceneObjectTree } from "../../scene/sceneDispose";
import { devLog } from "../../scene/sceneConstants";
import { logger } from "../../../utils/logger";

export function createExternalForceVizController({ externalForceVizRef, sceneRef }) {
  const disposeAndRemove = (obj) => {
    if (!obj) return;
    try {
      releaseAndDisposeSceneObjectTree(obj);
    } catch {
      // Best-effort cleanup; disposal must not break unmount.
    }
    try {
      if (obj.parent) obj.parent.remove(obj);
    } catch {
      // Best-effort cleanup; parent removal may race scene teardown.
    }
  };

  const clearExternalForceViz = () => {
    try {
      const m = externalForceVizRef.current?.byBodyUuid;
      if (!m) return;
      for (const entry of m.values()) {
        disposeAndRemove(entry?.arrow);
      }
      m.clear();
    } catch {
      // Best-effort cleanup; stale force markers are safe to ignore.
    }
  };

  const mujocoVecToViewer = (v) => {
    const fx = Number(v?.[0]) || 0;
    const fy = Number(v?.[1]) || 0;
    const fz = Number(v?.[2]) || 0;
    // MuJoCo: +Z up. Viewer: X right, Y up, Z forward. Existing mapping is [x, z, -y].
    return new THREE.Vector3(fx, fz, -fy);
  };

  const onExternalForcePulse = (e) => {
    const detail = e?.detail || {};
    const bodyUuid = detail?.bodyUuid ? String(detail.bodyUuid) : "";
    if (!bodyUuid) return;
    try {
      if (typeof window !== "undefined" && window.__debugExternalForces === true) {
        devLog("[Scene] external force pulse", {
          bodyUuid,
          force: detail?.force,
          durationSeconds: detail?.durationSeconds,
        });
      }
    } catch {
      // Debug logging must not affect force visualization.
    }
    const force = Array.isArray(detail?.force) ? detail.force : [0, 0, 0];
    const durationSeconds = Number(detail?.durationSeconds);
    const ttlMs = Number.isFinite(durationSeconds)
      ? Math.max(300, Math.min(2000, durationSeconds * 1000 * 12))
      : 600;
    const now = performance.now();
    const expiresAtMs = now + ttlMs;

    const byBodyUuid = externalForceVizRef.current.byBodyUuid;
    let entry = byBodyUuid.get(bodyUuid) || null;
    if (!entry || !entry.arrow) {
      const dir = new THREE.Vector3(1, 0, 0);
      const color = 0xffc44d;
      const arrow = new THREE.ArrowHelper(dir, new THREE.Vector3(0, 0, 0), 0.08, color);
      arrow.name = `externalForceArrow:${bodyUuid}`;
      arrow.renderOrder = 9999;
      try {
        if (arrow.line?.material) {
          arrow.line.material.depthTest = false;
          arrow.line.material.depthWrite = false;
          arrow.line.material.transparent = true;
          arrow.line.material.opacity = 0.95;
        }
      } catch (error) {
        logger.warn("[Scene] Unexpected error during scene update", error);
      }
      try {
        if (arrow.cone?.material) {
          arrow.cone.material.depthTest = false;
          arrow.cone.material.depthWrite = false;
          arrow.cone.material.transparent = true;
          arrow.cone.material.opacity = 0.95;
        }
      } catch (error) {
        logger.warn("[Scene] Unexpected error during scene update", error);
      }
      // If the event arrives before the scene is ready, we may not be able to add yet.
      // We'll attempt to add again during pose updates.
      try {
        if (sceneRef.current) sceneRef.current.add(arrow);
      } catch (error) {
        logger.warn("[Scene] Unexpected error during scene update", error);
      }
      entry = { arrow, force, expiresAtMs, bodyUuid };
      byBodyUuid.set(bodyUuid, entry);
    } else {
      entry.force = force;
      entry.expiresAtMs = expiresAtMs;
    }
  };

  const onActuatorControlPulse = (e) => {
    const detail = e?.detail || {};
    const bodyUuid = detail?.bodyUuid ? String(detail.bodyUuid) : "";
    if (!bodyUuid) return;
    const controlDeltaNorm = Number(detail?.controlDeltaNorm) || 0;
    const vizForce = Array.isArray(detail?.vizForce) ? detail.vizForce : null;
    // Reuse the same arrow channel as external forces (amber pulse marker).
    const force =
      vizForce && vizForce.length >= 3
        ? [Number(vizForce[0]) || 0, Number(vizForce[1]) || 0, Number(vizForce[2]) || 0]
        : [Math.max(6, controlDeltaNorm * 32), 0, Math.max(6, controlDeltaNorm * 20)];
    onExternalForcePulse({
      detail: {
        bodyUuid,
        force,
        durationSeconds: Number(detail?.durationSeconds) || 0.2,
      },
    });
  };

  return {
    clearExternalForceViz,
    disposeAndRemove,
    mujocoVecToViewer,
    onActuatorControlPulse,
    onExternalForcePulse,
  };
}
